import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { addToast } from 'components/Toast/Toast'
import { Modal } from 'components-ref/molecules/modal/modal'
import { acceptTrialLink } from 'services/endpoints/settings/general/subscription'
import colors from 'styles/colors'

interface FreeTrialModalProps {
    onClose: () => void
    trialCode: string
    trial: any
    user: any
}

export const FreeTrialModal = ({
    onClose,
    trialCode,
    trial,
    user
}: FreeTrialModalProps) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const displayName = user.display_name

    const trialMutation = useMutation(
        async () => await acceptTrialLink(trialCode),
        {
            onSuccess: async () => {

/* 
                await queryClient.invalidateQueries([
                    'profile',
                    user.username
                ])
 */
                onClose()
                navigate(`/${user.username}`, {
                    replace: true
                })

            },
            onError: (error: any) => {
                const message =
                    error?.response?.data?.error?.message ||
                    error?.response?.data?.message ||
                    'An error occurred, please try again.'

                addToast(
                    'error',
                    message,
                    undefined,
                    true
                )
            }
        }
    )

    const submit = () => {
        if (trialMutation.isLoading) return

        trialMutation.mutate()
    }

    return (
        <Modal
            title={t('Accept Free Trial')}
            onClose={onClose}
            hasShadow={true}
            paddingBottom={0}
            hideCloseIcon={true}
        >
            <UserInfo>
                <UserInfoImage>
                    {user.avatar?.url && (
                        <img
                            src={user.avatar.url}
                            alt='avatar'
                        />
                    )}
                </UserInfoImage>

                <UserInfoNames>
                    <Name>{displayName}</Name>
                    <UserName>
                        @{user.username.toLowerCase()}
                    </UserName>
                </UserInfoNames>
            </UserInfo>

            <Confirmation>
                Please confirm that you are accepting a{' '}
                <TrialDuration>
                    {trial.duration} Day
                </TrialDuration>{' '}
                free trial subscription to {displayName}.
            </Confirmation>

            <ButtonContainer>
                <Button
                    onClick={onClose}
                    disabled={trialMutation.isLoading}
                >
                    Cancel
                </Button>

                <YesButton
                    onClick={submit}
                    disabled={trialMutation.isLoading}
                >
                    {trialMutation.isLoading
                        ? 'Starting...'
                        : 'Subscribe'}
                </YesButton>
            </ButtonContainer>
        </Modal>
    )
}

const UserInfo = styled.div`
    margin: 2.5rem 0;
    display: flex;
`

const UserInfoImage = styled.div`
    width: 3.6rem;
    height: 3.6rem;
    border-radius: 100%;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 100%;
    }
`

const UserInfoNames = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 1rem;
`

const Name = styled.div`
    font-size: 1.4rem;
    font-weight: 600;
    color: ${colors.blueDark};
`

const Confirmation = styled.div`
    font-family: 'Montserrat', sans-serif;
    font-size: 1.4rem;
    font-weight: 400;
    color: ${colors.blueDark};
    line-height: 2rem;
`

const TrialDuration = styled.span`
    font-weight: 500;
`

const UserName = styled.div`
    color: ${colors.slateBlueGray};
    font-size: 1.2rem;
`

const Button = styled.button`
    width: 9.8rem;
    height: 3.6rem;
    background: ${colors.whiteSmoke};
    border-radius: 2rem;
    color: ${colors.grey};
    font-size: 1.4rem;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    border: none;
    cursor: pointer;

    &:disabled {
        cursor: default;
        opacity: 0.6;
    }
`

const YesButton = styled.button`
    width: 15.1rem;
    height: 3.6rem;
    background: ${colors.black157deg};
    border-radius: 16rem;
    color: ${colors.white};
    font-size: 1.4rem;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    margin-left: 1rem;
    border: none;
    cursor: pointer;

    &:disabled {
        cursor: default;
        opacity: 0.6;
    }
`

const ButtonContainer = styled.div`
    margin-top: 1.5rem;
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
`